/**
 * A patch minifier takes a value and a patch and returns the minimum patch that
 * would make the same changes as the given patch.
 *
 * `undefined` is used in two distinct ways, to represent that a value is
 * unknown **OR** to represent that a patch makes no change. This allows patch
 * minifiers to be used in forms that only edit part of a value.
 *
 * When the previous value is unknown (represented by `undefined`), patch
 * minifiers should assume the worst case and return a patch that would make the
 * same changes as the given patch on all possible values.
 *
 * @template P The type of the patch.
 * @template V The type of a value that can be patched. Patches and values can
 *             have incompatible types. For example the value might be an array,
 *             but the patch might be the additions and removals.
 */
export type PatchMinifier<P, V = P> = (prev: V, patch: P) => P;

export function minPatchPrimitive<T>(
	prev: T | undefined,
	patch: T | undefined
) {
	if (prev === undefined || prev !== patch) {
		return patch;
	}
}

/**
 * This is used for a field that is always required in a patch. The `id` of an
 * entity is an example.
 */
export function minPatchRequired<T>(_prev: T | undefined, patch: T) {
	return patch;
}

function baseMinPatch<T>(
	minPatch: (prev: T, patch: T) => T | undefined
): PatchMinifier<T | undefined> {
	return (prev, patch) => {
		if (prev === undefined || patch === undefined) {
			return patch;
		}

		// Note, short cutting on `prev === patch` does not work with all minifiers.
		// Some have required properties, even when there is no change.
		if (prev === patch) {
			return undefined;
		}

		return minPatch(prev, patch);
	};
}

function isDefinedEntry<K, V>(
	entry: readonly [K, V | undefined]
): entry is readonly [K, V] {
	return entry[1] !== undefined;
}

function minPatchEntries<P extends Record<string, unknown>>(
	toEntries: (prev: P, patch: P) => readonly [keyof P, P[keyof P] | undefined][]
) {
	return (prev: P, patch: P) => {
		const entries = toEntries(prev, patch).filter(isDefinedEntry);

		if (entries.length === 0) {
			return undefined;
		}

		return Object.fromEntries(entries) as P;
	};
}

type EmptyObject = Record<PropertyKey, never>;

type ObjectPatch = Partial<Record<string, unknown>> | undefined;

type ObjectPatchSchema<P extends ObjectPatch> = {
	[K in keyof NonNullable<P>]-?: PatchMinifier<NonNullable<P>[K]>;
};

type ObjectPatchMinifier<P extends ObjectPatch> = PatchMinifier<
	EmptyObject extends P ? P | undefined : P
>;

export function minPatchObject<P extends ObjectPatch>(
	schema: ObjectPatchSchema<P>
) {
	return minPatchEntries((prev, patch) =>
		Object.entries(schema).map(([key, minPatchValue]) => [
			key,
			minPatchValue(prev?.[key], patch?.[key]),
		])
	) as ObjectPatchMinifier<P>;
}

export function minPatchRecord<K extends string, Vp>(
	keys: Iterable<K>,
	minPatchValue: PatchMinifier<Vp>
) {
	return minPatchEntries<Record<K, Vp>>((prev, patch) =>
		Array.from(
			keys,
			(key) => [key, minPatchValue(prev?.[key], patch?.[key])] as const
		)
	) as PatchMinifier<
		undefined extends Vp ? Partial<Record<K, Vp>> | undefined : Record<K, Vp>
	>;
}

export function minPatchSetLikeArray<P>(
	areElementsEqual?: (x: P, y: P) => boolean
) {
	return baseMinPatch<P[]>((prev, patch) =>
		haveSameElements(prev, patch, areElementsEqual) ? undefined : patch
	);
}

/**
 * Returns `true` if the two arrays have the same elements, regardless of order
 * and multiple entries.
 */
function haveSameElements<T>(
	xs: readonly T[],
	ys: readonly T[],
	areElementsEqual?: (x: T, y: T) => boolean
) {
	return (
		isSubSet(xs, ys, areElementsEqual) && isSubSet(ys, xs, areElementsEqual)
	);
}

function isSubSet<T>(
	xs: readonly T[],
	ys: readonly T[],
	areElementsEqual?: (x: T, y: T) => boolean
) {
	return xs.every((x) =>
		areElementsEqual ? ys.some((y) => areElementsEqual(x, y)) : ys.includes(x)
	);
}
